import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { SUPPRESS_ERROR_TOAST } from '@core/http/http-context-tokens';
import { Observable } from 'rxjs';
import { NominatimPlaceDto } from '../../dtos/nominatim-place.dto';

// The only datasource in the app that calls a host other than our own API, so it deliberately does
// NOT inject API_URL. Nominatim is keyless but rate-limited: its usage policy caps callers at one
// request per second and rules out per-keystroke autocomplete, which is why the picker searches on
// an explicit submit rather than on every keystroke.
const NOMINATIM_SEARCH_URL = 'https://nominatim.openstreetmap.org/search';
const NOMINATIM_REVERSE_URL = 'https://nominatim.openstreetmap.org/reverse';
const MAX_RESULTS = 5;
// Roughly building-level precision -- matches the picker's marker-drag granularity.
const REVERSE_ZOOM = 18;

@Injectable()
export class NominatimRemoteDatasource {
  private readonly http = inject(HttpClient);
  private readonly transloco = inject(TranslocoService);

  public search(query: string): Observable<NominatimPlaceDto[]> {
    return this.http.get<NominatimPlaceDto[]>(NOMINATIM_SEARCH_URL, {
      params: {
        q: query,
        format: 'jsonv2',
        limit: MAX_RESULTS,
        // Needed to read the matched house_number back out -- see NominatimPlaceDto.
        addressdetails: 1,
        'accept-language': this.transloco.getActiveLang(),
      },
      // The picker renders its own inline error, so the global error toast would be noise.
      context: new HttpContext().set(SUPPRESS_ERROR_TOAST, true),
    });
  }

  // Nominatim answers an unresolvable point with a 200 body like `{ error: "Unable to geocode" }`
  // rather than the NominatimPlaceDto shape, so callers must treat a malformed response as "no address".
  public reverse(latitude: number, longitude: number): Observable<NominatimPlaceDto> {
    return this.http.get<NominatimPlaceDto>(NOMINATIM_REVERSE_URL, {
      params: {
        lat: latitude,
        lon: longitude,
        format: 'jsonv2',
        zoom: REVERSE_ZOOM,
        addressdetails: 1,
        'accept-language': this.transloco.getActiveLang(),
      },
      context: new HttpContext().set(SUPPRESS_ERROR_TOAST, true),
    });
  }
}
